import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Video, CalendarCheck2, CheckCircle2, AlertTriangle, Loader2, Mail } from "lucide-react";

// `setupHint` shows when the server has no credentials for a provider. Zoom is
// what the module is for, so a missing Zoom app is worth chasing an admin about.
// Outlook is a deliberate extra — its hint says what you're missing out on rather
// than reading like something is broken.
const PROVIDER_META = {
  zoom: {
    icon: Video,
    blurb: "Create the meeting on your own Zoom account — HQ never needs a shared link.",
    setupHint:
      "An admin needs to finish the Zoom app setup before anyone can connect.",
    optional: false,
  },
  microsoft: {
    icon: CalendarCheck2,
    blurb: "Put every meeting on your Outlook calendar and check your free/busy before booking.",
    setupHint:
      "Not switched on yet. Booking and client invites work without it — this would also drop each meeting on your own calendar and warn you about clashes.",
    optional: true,
  },
};

const ERROR_COPY = {
  consent_denied: "You cancelled the connection.",
  invalid_state: "That connection link expired — try again.",
  missing_code: "The provider didn't return an authorization code.",
  token_exchange_failed: "The provider rejected the connection. Check the app credentials and redirect URI.",
  no_refresh_token: "The provider didn't grant offline access, so HQ can't keep the connection alive.",
  server_error: "Something went wrong finishing the connection.",
};

/**
 * Zoom / Outlook connection cards. Rendered on the Meetings hub and in
 * Settings › Integrations — both read the same per-user state, so connecting in
 * one place shows up in the other.
 */
export default function MeetingIntegrationsCard({ returnTo = "/meetings", onChange, compact = false }) {
  const [items, setItems] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [senderEmail, setSenderEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/meetings/integrations");
      setItems(res.data?.items || []);
      setEmailConfigured(Boolean(res.data?.email_configured));
      setSenderEmail(res.data?.sender_email || "");
      onChange?.(res.data?.items || []);
    } catch (e) {
      toast.error("Could not load your meeting integrations");
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  useEffect(() => {
    load();
  }, [load]);

  // Read the OAuth callback result off the URL, then clean it up.
  useEffect(() => {
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");
    if (!connected && !error) return;
    if (connected) {
      toast.success(`${connected === "zoom" ? "Zoom" : "Outlook"} connected`);
    } else {
      toast.error(ERROR_COPY[error] || "Could not finish connecting");
    }
    const next = new URLSearchParams(searchParams);
    ["connected", "error", "provider"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const connect = async (provider) => {
    setBusy(provider);
    try {
      const res = await apiClient.post(
        `/meetings/integrations/${provider}/authorize?return_to=${encodeURIComponent(returnTo)}`
      );
      const url = res.data?.authorize_url;
      if (!url) throw new Error("No authorize URL");
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the connection");
      setBusy(null);
    }
  };

  const disconnect = async (provider, label) => {
    setBusy(provider);
    try {
      await apiClient.post(`/meetings/integrations/${provider}/disconnect`);
      toast.success(`${label} disconnected`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading integrations…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="meeting-integrations">
      {items.map((item) => {
        const meta = PROVIDER_META[item.provider] || {};
        const Icon = meta.icon || Video;
        const needsReauth = item.status === "reauth_required";
        return (
          <Card
            key={item.provider}
            className="border border-border p-4 sm:p-5"
            data-testid={`integration-${item.provider}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div
                className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                  item.connected && !needsReauth ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-muted-foreground"
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base text-foreground">{item.label}</span>
                  {item.connected && !needsReauth && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[1.5px] text-emerald-700">
                      <CheckCircle2 className="w-3 h-3" /> Connected
                    </span>
                  )}
                  {needsReauth && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[1.5px] text-red-700">
                      <AlertTriangle className="w-3 h-3" /> Reconnect needed
                    </span>
                  )}
                  {!item.configured && (
                    <span className="text-[10px] uppercase tracking-[1.5px] text-muted-foreground">
                      {meta.optional ? "Optional · not on yet" : "Not set up on the server"}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {item.connected && item.account_email ? (
                    <>
                      {item.account_email}
                      {item.account_name ? ` · ${item.account_name}` : ""}
                    </>
                  ) : !item.configured ? (
                    meta.setupHint
                  ) : (
                    meta.blurb
                  )}
                </p>
                {needsReauth && item.last_error && (
                  <p className="text-xs text-red-700 mt-1">{item.last_error}</p>
                )}
              </div>

              <div className="shrink-0">
                {item.connected ? (
                  <div className="flex items-center gap-2">
                    {needsReauth && (
                      <Button
                        size="sm"
                        onClick={() => connect(item.provider)}
                        disabled={busy === item.provider}
                        data-testid={`reconnect-${item.provider}`}
                      >
                        Reconnect
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => disconnect(item.provider, item.label)}
                      disabled={busy === item.provider}
                      data-testid={`disconnect-${item.provider}`}
                    >
                      {busy === item.provider ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Disconnect"}
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => connect(item.provider)}
                    disabled={!item.configured || busy === item.provider}
                    data-testid={`connect-${item.provider}`}
                  >
                    {busy === item.provider ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      `Connect ${item.label}`
                    )}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        );
      })}

      {!compact && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground pt-1">
          <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <p>
            {emailConfigured
              ? `Invites are sent from ${senderEmail} with your address as reply-to, and carry a calendar attachment so the meeting lands in the client's calendar either way.`
              : "Email sending is not configured on this server, so HQ can create the meeting but cannot email the invite."}
          </p>
        </div>
      )}
    </div>
  );
}
